import classnames from 'classnames';
import { ErrorBoundary } from 'components/Errors/ErrorBoundary';
import LoadingScreen from 'components/LoadingScreen';
import { atom, useAtom, useAtomValue } from 'jotai';
import React, { Suspense, useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import { Row, Col } from 'reactstrap';

import { excludedQIDsAtom } from './tabs/ExcludedQIDsConfigTab/state';
import { ConfigTabType } from './types';

// Lazy load tab components
const IgnoredQIDsConfigTab = React.lazy(
  () => import('./tabs/IgnoredQIDsConfigTab'),
);
const ExcludedQIDsConfigTab = React.lazy(
  () => import('./tabs/ExcludedQIDsConfigTab'),
);
const CAActivationKeysTab = React.lazy(
  () => import('./tabs/CAActivationKeysTab'),
);

// Create an atom for managing the current tab state
export const currentConfigTabAtom = atom<ConfigTabType>(
  ConfigTabType.IgnoredQIDs,
);

interface ConfigTabContentProps {
  currentTab: ConfigTabType;
}

export const ConfigTabContent: React.FC<ConfigTabContentProps> = ({
  currentTab,
}) => {
  const excludedQIDs = useAtomValue(excludedQIDsAtom);

  const hasExcludedQIDs =
    excludedQIDs?.excludedWAQIDs?.length > 0 ||
    excludedQIDs?.excludedEPQIDs?.length > 0 ||
    excludedQIDs?.excludedCAQIDs?.length > 0;
  return (
    <>
      {currentTab === ConfigTabType.IgnoredQIDs ? (
        <IgnoredQIDsConfigTab />
      ) : hasExcludedQIDs && currentTab === ConfigTabType.ExcludedQIDs ? (
        <ExcludedQIDsConfigTab />
      ) : currentTab === ConfigTabType.CAActivationKeys ? (
        <CAActivationKeysTab />
      ) : null}
    </>
  );
};

export const ConfigTabList: React.FC<{
  currentTab: ConfigTabType;
  toggle: (tab: ConfigTabType) => void;
}> = ({ currentTab, toggle }) => {
  const excludedQIDs = useAtomValue(excludedQIDsAtom);

  const hasExcludedQIDs =
    excludedQIDs?.excludedWAQIDs?.length > 0 ||
    excludedQIDs?.excludedEPQIDs?.length > 0 ||
    excludedQIDs?.excludedCAQIDs?.length > 0;

  const tabTypes = Object.values(ConfigTabType);
  if (!hasExcludedQIDs) {
    tabTypes.splice(tabTypes.indexOf(ConfigTabType.ExcludedQIDs), 1);
  }
  return (
    <>
      {tabTypes.map((tabType) => (
        <div
          key={tabType}
          className={classnames('p-3 mb-2 rounded transition-all', {
            'bg-primary text-white shadow-sm': currentTab === tabType,
            'bg-light hover:bg-gray-100': currentTab !== tabType,
          })}
          onClick={() => toggle(tabType)}
          style={{
            cursor: 'pointer',
            borderRadius: '8px',
            transition: 'all 0.2s ease',
          }}
        >
          {tabType
            .replace(/-/g, ' ')
            .replace(/\b\w/g, (char) => char.toUpperCase())}
        </div>
      ))}
    </>
  );
};

const CompanyConfiguration: React.FC = () => {
  const [currentTab, setCurrentTab] = useAtom(currentConfigTabAtom);

  useEffect(() => {
    // Handle URL hash for tab navigation
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      const tabType = Object.values(ConfigTabType).find(
        (type) => type.toLowerCase().replace(/ /g, '-') === hash,
      );
      if (tabType) {
        setCurrentTab(tabType);
      }
    };

    // Set initial tab from URL hash
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [setCurrentTab]);

  const toggle = (tab: ConfigTabType) => {
    if (currentTab !== tab) {
      window.location.hash = tab.toLowerCase().replace(/ /g, '-');
      setCurrentTab(tab);
    }
  };

  return (
    <div className="content">
      <Row className="h-100">
        <Col xs="10">
          <ErrorBoundary>
            <Suspense fallback={<LoadingScreen />}>
              <ConfigTabContent currentTab={currentTab} />
            </Suspense>
          </ErrorBoundary>
        </Col>
        <Col
          xs="2"
          className="border-left overflow-y-auto"
          style={{
            maxHeight: 'calc(100vh - 10rem)',
            overflowY: 'auto',
            height: '100%',
            padding: '1rem',
          }}
        >
          <Suspense fallback={<LoadingScreen />}>
            <ConfigTabList currentTab={currentTab} toggle={toggle} />
          </Suspense>
        </Col>
      </Row>
      <ToastContainer />
    </div>
  );
};

export default CompanyConfiguration;
